import React from "react";
import {
  TableCompData,
  CardComp,
  ButtonComp,
  ModalCompAdvanceUpdate,
} from "../../../components";
import EmptyTable from "../../../components/Common/EmptyTable";
import LoanRepaymentRecord from "./LoanRepaymentRecord";
import LoanLateFees from "./LoanLateFees";
import { formatMoney } from "../../../utils";
import moment from "moment";
import { BsCalendar2Week } from "react-icons/bs";
import { BsArrowLeft } from "react-icons/bs";
import { useNavigate, useParams } from "react-router-dom";
import { convertToTitleCase } from "../../../utils/helpers";
import { installmentBalance } from "../../../components/Loan/partialRepayment";

/** Formal (schedule-based) admin loan repayment breakdown. */
const FormalSingleLoanRepaymentBreakdownByMember = ({ data, isLoading }) => {
  const navigate = useNavigate();
  const { loanRequestId } = useParams();
  const [modalPage, setModalPage] = React.useState("");
  const [loanRepaymentDetails, setLoanRepaymentDetails] = React.useState("");

  const useModal = [
    {
      name: "Record Loan",
      component: (
        <LoanRepaymentRecord
          Back={() => setModalPage()}
          loanRepaymentDetails={loanRepaymentDetails}
          partialEnabled={Boolean(data?.partial_repayment_enabled)}
          loanOutstanding={data?.outstanding_balance}
          lateFeeBalance={data?.late_fee_balance}
        />
      ),
    },
  ];

  const column = [
    {
      name: "S/N",
      selector: (_, index) => <span className="text-muted">{index + 1}</span>,
      width: "60px",
    },
    {
      name: "LOAN AMOUNT",
      selector: (row) => {
        const amount = parseFloat(row?.amount || 0);
        const roundedAmount = Math.round(amount * 100) / 100;
        return formatMoney(roundedAmount.toFixed(2));
      },
      minWidth: "180px",
    },
    {
      name: "PAID",
      selector: (row) => formatMoney(parseFloat(row?.amount_paid || 0).toFixed(2)),
      minWidth: "150px",
    },
    {
      name: "BALANCE",
      selector: (row) => formatMoney(installmentBalance(row).toFixed(2)),
      minWidth: "150px",
    },
    {
      name: "DUE DATE",
      selector: (row) => {
        const selectedDateStr = row?.due_at;
        if (!selectedDateStr) return <span className="text-muted">N/A</span>;
        const selectedDate = moment(selectedDateStr, "YYYY-MM-DD");
        const today = moment().startOf("day");
        const daysDifference = selectedDate.diff(today, "days");
        const isOverdue = daysDifference < 0;
        const isDueSoon = daysDifference >= 0 && daysDifference <= 7;
        const daysText = Math.abs(daysDifference) === 1 ? "day" : "days";
        const status = row?.breakdown_status || row?.status || "";
        const showDaysBadge =
          status === "PENDING" ||
          status === "OVERDUE" ||
          status === "PARTIAL" ||
          status === "";
        return (
          <div className="d-flex align-items-center gap-2">
            <BsCalendar2Week size={20} className="text-muted" />
            <span className="fw-semibold">
              {moment(row?.due_at).format("DD-MMM-YYYY")}
            </span>
            {showDaysBadge && (
              <div
                className={`px-2 py-1 rounded-pill text-black1 small ${
                  isOverdue
                    ? "bg-danger"
                    : isDueSoon
                    ? "bg-warning"
                    : "bg-success"
                }`}
              >
                {isOverdue
                  ? `${Math.abs(daysDifference)} ${daysText} overdue`
                  : isDueSoon
                  ? `Due in ${daysDifference} ${daysText}`
                  : daysDifference === 0
                  ? "Due today"
                  : `${daysDifference} ${daysText} remaining`}
              </div>
            )}
          </div>
        );
      },
      minWidth: "280px",
    },
    {
      name: "STATUS",
      selector: (row) => {
        const status = row?.breakdown_status || row?.status || "UNKNOWN";
        const statusColor =
          status === "OVERDUE"
            ? "#FF4343"
            : status === "PAID" || status === "COMPLETED"
            ? "#19BC31"
            : status === "PENDING"
            ? "#FFA500"
            : status === "PARTIAL"
            ? "#3B82F6"
            : "#A1A0A0";
        return (
          <div
            className="px-3 py-1 rounded-pill text-white text-center"
            style={{ backgroundColor: statusColor }}
          >
            {convertToTitleCase(status)}
          </div>
        );
      },
      width: "120px",
    },
    {
      name: "ACTION",
      cell: (row) =>
        row?.status === "PAID" ? (
          <span className="text-muted">Paid</span>
        ) : (
        <div className="text-center">
          <ButtonComp
            onClick={() => {
              setLoanRepaymentDetails(row);
              setModalPage("Record Loan");
            }}
            btnText={data?.partial_repayment_enabled ? "Record payment" : "Record loan"}
            btnClassName="bg-primary1 px-3 fw-2 h6 py-2 text-white rounded"
            style={{ whiteSpace: "nowrap", minWidth: "fit-content" }}
          />
        </div>
        ),
      // "Record payment" does not wrap, so the column must be wide enough for it plus the button padding
      minWidth: "200px",
    },
  ];

  return (
    <section className="px-lg-3 min-vh-100 mb-3">
      <div className="space-y-4">
        <CardComp cardClassName="border-0 shadow-sm mb-4">
          <div className="d-flex align-items-center gap-3">
            <BsArrowLeft
              size={24}
              onClick={() => navigate(-1)}
              className="cursor-pointer text-primary1"
            />
            <div className="flex-grow-1">
              <h4 className="fw-bold mb-1 text-primary1">
                {data?.loan_type?.name || "Loan"} Repayment
              </h4>
              {data?.member && (
                <p className="text-muted mb-0 small">
                  Member: {data.member.first_name} {data.member.last_name}
                  {data.member.member_no && ` (${data.member.member_no})`}
                </p>
              )}
            </div>
          </div>
        </CardComp>

        <CardComp cardClassName="border-0 shadow-sm mb-4">
          <div className="row g-4 p-3">
            <div className="col-md-3 col-6">
              <div className="text-center">
                <p className="text-muted mb-2 small fw-semibold">Loan Tenor</p>
                <div className="d-flex align-items-center justify-content-center gap-2">
                  <span className="text-3xl fw-bold text-dark">
                    {data?.duration || 0}
                  </span>
                  <span className="text-muted">
                    {data?.duration_type
                      ? convertToTitleCase(data.duration_type.toLowerCase())
                      : ""}
                  </span>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-6">
              <div className="text-center border-start border-end border-md-0 px-md-0 px-3">
                <p className="text-muted mb-2 small fw-semibold">
                  Loan Interest
                </p>
                <div className="d-flex align-items-center justify-content-center gap-2">
                  <span className="text-3xl fw-bold text-dark">
                    {data?.loan_type?.rate || 0}%
                  </span>
                  <span className="text-muted">Annual</span>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-6">
              <div className="text-center border-start border-end border-md-0 px-md-0 px-3">
                <p className="text-muted mb-2 small fw-semibold">
                  Overdue Repayment
                </p>
                <div className="d-flex align-items-center justify-content-center gap-2">
                  <span className="text-3xl fw-bold text-danger">
                    {data?.overdue_repayment || 0}
                  </span>
                  <span className="text-danger fw-semibold">Overdue</span>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-6">
              <div className="text-center border-start border-end border-md-0 px-md-0 px-3">
                <p className="text-muted mb-2 small fw-semibold">
                  Pending Repayment
                </p>
                <div className="d-flex align-items-center justify-content-center gap-2">
                  <span className="text-3xl fw-bold text-dark">
                    {data?.pending_repayment || 0}
                  </span>
                  <span className="text-dark fw-semibold">Pending</span>
                </div>
              </div>
            </div>
          </div>
        </CardComp>

        <CardComp cardClassName="border-0 shadow-sm">
          <div className="mb-4">
            <h5 className="fw-bold mb-1 text-primary1">Repayment Breakdown</h5>
            <p className="text-muted mb-0 small">
              Detailed schedule of all repayment installments
            </p>
          </div>
          <TableCompData
            marginBottom="1rem"
            columns={column}
            data={data?.breakdowns || []}
            message={
              isLoading ? (
                <div className="py-4 text-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <EmptyTable Message="There are currently no repayment breakdowns available" />
              )
            }
          />
        </CardComp>

        <LoanLateFees
          rule={data?.late_fee_rule}
          fees={data?.late_fees || []}
          balance={Number(data?.late_fee_balance) || 0}
        />
      </div>

      <ModalCompAdvanceUpdate
        pageName={modalPage}
        arrayComp={useModal}
        handleClose={() => setModalPage()}
        centered
        size="lg"
      />
    </section>
  );
};

export default FormalSingleLoanRepaymentBreakdownByMember;
